/**
 * useCanvasEditorState - Shared state management for canvas editors
 *
 * Extracts common state patterns used by block and screen canvas editors:
 * - Canvas handle management
 * - Selection state
 * - Loading/sync indicators
 * - Command manager instance
 * - Confirmation dialog state
 *
 * @param mode - Canvas mode ('blocks' | 'screens')
 * @returns State object with all setters and derived values
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { CommandManager } from '@modules/reactFlow/commands/CommandManager';
import type { ReactFlowCanvasHandle } from '@modules/reactFlow/types/flow-canvas-types';

export type CanvasMode = 'blocks' | 'screens';

export interface ConfirmDialog {
  message: string;
  details?: string;
  resolve: (confirmed: boolean) => void;
}

export interface CanvasEditorState {
  // Canvas control
  canvasHandle: ReactFlowCanvasHandle | null;
  setCanvasHandle: (handle: ReactFlowCanvasHandle | null) => void;
  handleCanvasReady: (handle: ReactFlowCanvasHandle) => void;

  // Selection
  selectedScreenIds: string[];
  setSelectedScreenIds: (ids: string[]) => void;

  // Launch entity
  launchScreenId: string | undefined;
  setLaunchScreenId: (id: string | undefined) => void;

  // Loading indicators
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isSyncing: boolean;
  setIsSyncing: (syncing: boolean) => void;
  syncOperation: string;
  setSyncOperation: (operation: string) => void;

  // Command manager
  commandManager: CommandManager;

  // Confirmation dialog
  confirmDialog: ConfirmDialog | null;
  setConfirmDialog: (dialog: ConfirmDialog | null) => void;
  showConfirmation: (message: string, details?: string) => Promise<boolean>;

  // State setters ref (for event handler context)
  settersRef: React.MutableRefObject<{
    setSelectedScreenIds: React.Dispatch<React.SetStateAction<string[]>>;
    setIsSyncing: React.Dispatch<React.SetStateAction<boolean>>;
    setSyncOperation: React.Dispatch<React.SetStateAction<string>>;
  }>;
}

/**
 * Hook for managing shared canvas editor state
 */
export const useCanvasEditorState = (mode: CanvasMode): CanvasEditorState => {
  // Canvas control
  const [canvasHandle, setCanvasHandle] = useState<ReactFlowCanvasHandle | null>(null);

  // Selection
  const [selectedScreenIds, setSelectedScreenIds] = useState<string[]>([]);

  // Launch entity
  const [launchScreenId, setLaunchScreenId] = useState<string | undefined>();

  // Loading indicators
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncOperation, setSyncOperation] = useState<string>('');

  // Command manager (singleton per component instance)
  const [commandManager] = useState(() => new CommandManager());

  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialog | null>(null);

  // Store state setters in ref for event handler context
  const settersRef = useRef({ setSelectedScreenIds, setIsSyncing, setSyncOperation });
  useEffect(() => {
    settersRef.current = { setSelectedScreenIds, setIsSyncing, setSyncOperation };
  });

  // Canvas ready callback
  const handleCanvasReady = useCallback((handle: ReactFlowCanvasHandle) => {
    console.log(`[${mode === 'blocks' ? 'BlockCanvasEditor' : 'ScreenCanvas'}] Canvas ready`);
    setCanvasHandle(handle);
  }, [mode]);

  // Confirmation dialog callback
  const showConfirmation = useCallback((message: string, details?: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmDialog({ message, details, resolve });
    });
  }, []);

  return {
    // Canvas control
    canvasHandle,
    setCanvasHandle,
    handleCanvasReady,

    // Selection
    selectedScreenIds,
    setSelectedScreenIds,

    // Launch entity
    launchScreenId,
    setLaunchScreenId,

    // Loading indicators
    isLoading,
    setIsLoading,
    isSyncing,
    setIsSyncing,
    syncOperation,
    setSyncOperation,

    // Command manager
    commandManager,

    // Confirmation dialog
    confirmDialog,
    setConfirmDialog,
    showConfirmation,

    // Setters ref
    settersRef,
  };
};
